import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Customer } from '../../types';
import { getCustomers, mapApiCustomer } from '../../services/customerService';
import { formatDate } from '../../utils/dateUtils';
import './MemberCard.css';

type CustomerSortKey = 'name' | 'email' | 'phone' | 'createdAt' | 'status';

const CustomerList: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<CustomerSortKey>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    setLoading(true);
    setError(null);
    try {
      const userData = localStorage.getItem('managerUserData');
      const storeId = userData ? JSON.parse(userData).store_id : undefined;
      const apiCustomers = await getCustomers(storeId);
      setCustomers(apiCustomers.map(mapApiCustomer));
    } catch (err: any) {
      console.error('Error loading customers:', err);
      setError(err.message || 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = useMemo(() => {
    let result = customers;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(c =>
        c.firstName.toLowerCase().includes(query) ||
        c.lastName.toLowerCase().includes(query) ||
        c.email.toLowerCase().includes(query) ||
        (c.mainPhone || '').includes(query)
      );
    }

    // Apply sort
    const sorted = [...result];
    const direction = sortDirection === 'asc' ? 1 : -1;

    sorted.sort((a, b) => {
      let comparison = 0;
      switch (sortKey) {
        case 'name':
          comparison = `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
          break;
        case 'email':
          comparison = a.email.localeCompare(b.email);
          break;
        case 'phone':
          comparison = (a.mainPhone || '').localeCompare(b.mainPhone || '');
          break;
        case 'createdAt':
          comparison = (a.createdAt || '').localeCompare(b.createdAt || '');
          break;
        case 'status':
          comparison = (a.banned === b.banned) ? 0 : (a.banned ? 1 : -1);
          break;
      }
      return comparison * direction;
    });

    return sorted;
  }, [searchQuery, customers, sortKey, sortDirection]);

  const handleSort = (key: CustomerSortKey) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const getSortIndicator = (key: CustomerSortKey) => {
    if (sortKey !== key) return null;
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  const handleCustomerClick = (customerId: number) => {
    navigate(`/customers/${customerId}`);
  };

  if (loading) {
    return <div className="no-results"><p>Loading customers...</p></div>;
  }

  if (error) {
    return (
      <div className="no-results">
        <p>{error}</p>
        <button onClick={loadCustomers} className="btn btn-secondary">Retry</button>
      </div>
    );
  }

  return (
    <>
      <div className="member-list-header">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          {searchQuery && (
            <button
              className="search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
            >
              &times;
            </button>
          )}
        </div>
      </div>

      <div className="member-list">
        {filteredCustomers.length === 0 ? (
          <div className="no-results">
            <p>No customers found{searchQuery ? ` matching "${searchQuery}"` : ''}</p>
          </div>
        ) : (
          <table className="member-table">
            <thead>
              <tr>
                <th className="sortable-header" onClick={() => handleSort('name')}>
                  Name{getSortIndicator('name')}
                </th>
                <th className="sortable-header" onClick={() => handleSort('email')}>
                  Email{getSortIndicator('email')}
                </th>
                <th className="sortable-header" onClick={() => handleSort('phone')}>
                  Phone{getSortIndicator('phone')}
                </th>
                <th className="sortable-header" onClick={() => handleSort('createdAt')}>
                  Created{getSortIndicator('createdAt')}
                </th>
                <th className="sortable-header" onClick={() => handleSort('status')}>
                  Status{getSortIndicator('status')}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map(customer => (
                <tr
                  key={customer.id}
                  onClick={() => handleCustomerClick(customer.id)}
                  className="member-row"
                >
                  <td className="member-name">
                    {customer.firstName} {customer.lastName}
                  </td>
                  <td>{customer.email}</td>
                  <td>{customer.mainPhone || '-'}</td>
                  <td>{customer.createdAt ? formatDate(customer.createdAt) : '-'}</td>
                  <td>
                    <span className={`status-badge status-badge--${customer.banned ? 'inactive' : 'active'}`}>
                      {customer.banned ? 'Banned' : 'Active'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="member-count">
        Showing {filteredCustomers.length} of {customers.length} customers
      </div>
    </>
  );
};

export default CustomerList;
